import * as React from 'react';
import { useCallback, useContext, useMemo, useState } from 'react';
import moment from 'moment/moment';

import { DD_SLASH_MMM_SLASH_YYYY } from '../../../../../constants/dateFormat';
import { MANAGE_REVENUE_EVENTS } from '../../../../../constants/userOperations';
import { userHasAccessTo } from '../../../../../services/auth';
import { formatDate } from '../../../../../services/date';
import { toBill } from '../../../../../services/financial';
import { ACTIONS_BUTTON } from '../../NewInvoice/InvoiceTable/TableConstants';
import { Amount } from '../../shared/amount-view/Amount/Amount';
import { studyTypeOptionsMap } from '../../shared/FinanceFilters/MultiSelectStudyType';
import { finStudyTypeFilterOperator } from '../../shared/FinanseTableMUI/CustomFilterOperators';
import { DefaultFinanceCell } from '../../shared/FinanseTableMUI/DefaultFinanceCell';
import { FinanceTableMUI } from '../../shared/FinanseTableMUI/FinanceTableMUI';
import {
  eventNumberComparator,
  formatDateWithoutUTC,
  prepareNameForCSV
} from '../../shared/FinanseTableMUI/FinanceTableServices';
import { RevenueContext } from '../RevenueContext';

import { RevenueTableRowMenu } from './RevenueTableRowMenu/RevenueTableRowMenu';

export const RevenueTable = () => {
  const { ledgerEvents } = useContext(RevenueContext);

  const getTogglableColumns = useCallback(
    columns => columns.filter(column => !['actions'].includes(column.field)).map(column => column.field),
    []
  );

  const stateFromLocalStorage = JSON.parse(localStorage?.getItem('REVENUE_TABLE_CONFIGURATION'));

  const [pinnedColumns, setPinnedColumns] = useState(
    stateFromLocalStorage?.pinnedColumns || {
      right: [ACTIONS_BUTTON]
    }
  );

  const handlePinnedColumnsChange = useCallback(updatedPinnedColumns => {
    setPinnedColumns({
      ...updatedPinnedColumns,
      right: [...updatedPinnedColumns.right.filter(column => column !== ACTIONS_BUTTON), ACTIONS_BUTTON]
    });
  }, []);

  const columns = useMemo(() => {
    const columns = [
      {
        field: 'eventNumber',
        headerName: 'Event ID',
        flex: 1,
        minWidth: 120,
        sortComparator: eventNumberComparator,
        valueGetter: (value, row) => {
          if (row.adjustmentSequence === 0) {
            return `${row.eventNumber}`;
          }
          return `${row.eventNumber}.${row.adjustmentSequence}`;
        }
      },
      {
        field: 'studyName',
        headerName: 'Study',
        flex: 1,
        minWidth: 105,
        renderCell: DefaultFinanceCell,
        valueFormatter: value => prepareNameForCSV(value)
      },
      {
        field: 'finStudyType',
        headerName: 'Study Type',
        flex: 1,
        minWidth: 145,
        renderCell: DefaultFinanceCell,
        valueFormatter: value => prepareNameForCSV(value),
        valueGetter: value => studyTypeOptionsMap.find(option => option.id === value)?.name || value,
        filterOperators: finStudyTypeFilterOperator
      },
      {
        field: 'siteName',
        headerName: 'Site',
        flex: 1,
        minWidth: 95,
        renderCell: DefaultFinanceCell,
        valueFormatter: value => prepareNameForCSV(value)
      },
      {
        field: 'patientId',
        headerName: 'Patient ID',
        flex: 1,
        minWidth: 130,
        renderCell: DefaultFinanceCell
      },
      {
        field: 'subjectId',
        headerName: 'Subject ID',
        flex: 1,
        minWidth: 135,
        renderCell: DefaultFinanceCell,
        valueFormatter: value => (value != null ? `[${value}]` : '')
      },
      {
        field: 'encounterName',
        headerName: 'Encounter',
        flex: 1,
        minWidth: 135,
        renderCell: DefaultFinanceCell,
        valueFormatter: value => prepareNameForCSV(value)
      },
      {
        field: 'elligoEventType',
        headerName: 'Budget Event Type',
        flex: 1,
        minWidth: 190,
        renderCell: DefaultFinanceCell
      },
      {
        field: 'itemGroupName',
        headerName: 'Item Group',
        flex: 1,
        minWidth: 140,
        renderCell: DefaultFinanceCell,
        valueFormatter: value => prepareNameForCSV(value)
      },
      {
        field: 'eventName',
        headerName: 'Event Name',
        flex: 1,
        minWidth: 150,
        renderCell: DefaultFinanceCell,
        valueFormatter: value => prepareNameForCSV(value)
      },
      {
        field: 'eventDate',
        headerName: 'Event Date',
        flex: 1,
        minWidth: 140,
        type: 'date',
        valueFormatter: value => formatDate.forCsv(value),
        valueGetter: value => value && new Date(value),
        renderCell: ({ row }) => {
          const content = moment(row.eventDate).format(DD_SLASH_MMM_SLASH_YYYY);
          return (
            <div className="overflow-ellipsis" title={content}>
              {content}
            </div>
          );
        }
      },
      {
        field: 'revenueDate',
        headerName: 'Revenue Date',
        flex: 1,
        minWidth: 155,
        type: 'date',
        valueFormatter: formatDateWithoutUTC,
        valueGetter: revenueDateValueGetter,
        renderCell: revenueDateRenderCell
      },
      {
        field: 'clientAmountWithOverhead',
        headerName: 'Gross',
        flex: 1,
        minWidth: 110,
        type: 'number',
        valueGetter: (value, row) => toBill(row.clientAmountWithOverhead),
        renderCell: ({ row }) => (
          <Amount coinsAmount={row.clientAmountWithOverhead} showDollarSign className="overflow-ellipsis" />
        )
      },
      {
        field: 'netRevenueAmount',
        headerName: 'Net',
        flex: 1,
        minWidth: 95,
        type: 'number',
        valueGetter: (value, row) => toBill(row.netRevenueAmount),
        renderCell: ({ row }) => (
          <Amount coinsAmount={row.netRevenueAmount} showDollarSign className="overflow-ellipsis" />
        )
      },
      {
        field: 'nonInvoiceable',
        headerName: 'Non-Invoiceable',
        minWidth: 180,
        valueGetter: value => (value ? 'Yes' : 'No')
      },
      {
        field: 'expenseVariableComment',
        headerName: 'Expense Comments',
        flex: 1,
        minWidth: 95,
        renderCell: DefaultFinanceCell,
        valueFormatter: value => prepareNameForCSV(value)
      }
    ];
    const actionColumn = {
      field: 'actions',
      type: 'actions',
      resizable: false,
      width: 50,
      renderCell: ({ row }) => <RevenueTableRowMenu row={row} />
    };
    return userHasAccessTo(MANAGE_REVENUE_EVENTS) ? [...columns, actionColumn] : columns;
  }, []);

  return (
    <FinanceTableMUI
      data-testid="revenue-table"
      localStorageTableName="REVENUE"
      rows={ledgerEvents}
      columns={columns}
      footerElementsCount={ledgerEvents.length}
      disableRowSelectionOnClick
      sx={{ padding: '0 40px 0 40px' }}
      getTogglableColumns={getTogglableColumns}
      initialState={{
        columns: {
          columnVisibilityModel: { nonInvoiceable: false }
        },
        sorting: {
          sortModel: [{ field: 'revenueDate', sort: 'desc' }]
        },
        pinnedColumns
      }}
      onPinnedColumnsChange={handlePinnedColumnsChange}
      pinnedColumns={pinnedColumns}
      tableVersion="1.02"
    />
  );
};

export const revenueDateRenderCell = ({ row }) => {
  const content = `${moment(row.revenueDate).format(DD_SLASH_MMM_SLASH_YYYY)}${row.revenueDateEdited ? '*' : ''}`;
  return (
    <div className="overflow-ellipsis" title={content}>
      {content}
    </div>
  );
};

export const revenueDateValueGetter = value => value && new Date(value);
